import { AppMode, cfgAppMode, cfgBrand, cfgPostauthNoSessionBehavior, cfgStage, getRealm, Stage, stateRealm } from '@infinitro/config';
import { jwtDecode } from 'jwt-decode';
import { get, writable } from 'svelte/store';
import { AsyncServerState, Logout, RefreshSession, Status } from './api/api';
import { Api, client } from './api_client';
import { getStoredPublicKey } from './device';
import { clearJwt, getJwt, refreshJwt, storeJwt } from './jwtStore';
import { locale } from './locale';
import { redirectNoloop } from './paths';
import { isPlayerChatOpen, sendMessage, showLoading, State, subscribeMessageState } from './uiStore';
const DEBUG = cfgStage() !== Stage.Prod;
const VERSION = import.meta.env.VITE_APP_VERSION;
function log(msg) {
    if (DEBUG)
        console.log(msg);
}
const sleep = (ms = 0) => new Promise((resolve) => setTimeout(resolve, ms));
export var NoSessionBehavior;
(function (NoSessionBehavior) {
    NoSessionBehavior["Redirect"] = "redirect";
    NoSessionBehavior["Modal"] = "modal";
    NoSessionBehavior["ModalWithRedirect"] = "modal_redirect";
})(NoSessionBehavior || (NoSessionBehavior = {}));
const parseNoSessionBehavior = (value) => {
    switch (value) {
        case 'redirect':
            return NoSessionBehavior.Redirect;
        case 'modal':
            return NoSessionBehavior.Modal;
        case 'modal_redirect':
            return NoSessionBehavior.ModalWithRedirect;
        default:
            log(`Invalid noSessionBehavior configuration ${value}, defaults to "redirect"`);
            return NoSessionBehavior.Redirect;
    }
};
export const noSessionBehavior = parseNoSessionBehavior(cfgPostauthNoSessionBehavior());
import { loggedIn } from './session-store';
export { loggedIn };
export const stateUsername = writable('');
export const statePlayerId = writable(undefined);
export const stateVisit = writable(undefined);
export { stateRealm };
let realm = getRealm();
// flush prod uses bare session no realm prefix (for v1 compatibility)
const useRealm = realm !== 'prod-flush';
export const getSessionKey = () => {
    if (!realm)
        realm = getRealm();
    if (useRealm)
        return `${realm}_session`;
    return 'session';
};
stateRealm.subscribe((value) => {
    if (realm === value)
        return;
    // this is only for local & staging environments where you can change endpoints dynamically
    // in production realm is fixed and cannot be changed
    log(`realm changed to ${value}`);
    realm = value;
    if (typeof window !== 'undefined') {
        initToken();
    }
});
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getServerRealm = async () => {
    try {
        const result = await AsyncServerState({ context: { api: 'public' } });
        if (result.data?.health?.realm) {
            //console.log(result);
            realm = result.data.health.realm;
            stateRealm.set(realm);
            return realm;
        }
    }
    catch (e) {
        console.log(`unable to get realm: ${e}`);
    }
    return undefined;
};
const isObjectIdRegex = /^[0-9a-fA-F]{24}$/i;
export const isValidObjectId = (value) => isObjectIdRegex.test(value);
// gets the player_id from the token which will be a uuid
// with temporary backwards compatibility with older tokens that have id as a mongo objectId
export const getPlayerId = (token) => {
    if (token?.player_id)
        return token.player_id;
    if (token?.id.length === 24) {
        return objectIdToUuid(token.id);
    }
    return null;
};
export const objectIdToUuid = (oid) => {
    if (oid.length !== 24 || !isValidObjectId(oid))
        return null;
    return `${oid.substring(0, 8)}-${oid.substring(8, 12)}-0000-0000-${oid.substring(12)}`;
};
// returns the unvalidated player_id and session_id from any stored JWT token
export const getUnvalidatedPlayerSession = async (_realm // eslint-disable-line @typescript-eslint/no-unused-vars
) => {
    // const __realm = _realm ?? realm;
    const [jwt, token] = await getJwt(getSessionKey());
    if (!jwt || !token)
        return [undefined, undefined, undefined, undefined, undefined];
    // if (token.iss && __realm && __realm !== realm) {
    // 	log(`token issuer is not realm, realm: ${realm}, issuer: ${token.iss}`);
    // 	return [undefined, undefined, undefined, undefined, undefined];
    // }
    return [getPlayerId(token), token.session_id, token.kid, token.iat, token.iss];
};
export const verifyToken = async (jwt, propogate, redirectTo) => {
    if (!realm)
        realm = getRealm();
    try {
        const token = jwtDecode(jwt);
        log(`verify token: ${JSON.stringify(token)}`);
        const now = Math.floor(Date.now() / 1000);
        if (!token.exp || now > token.exp) {
            // TODO: handle token renewal
            log(`token expired ${token.exp} < ${now}`);
            logOut();
            if (propogate)
                sendMessage({ state: State.LoggedIn, value: false });
        }
        else if (!token.iss && realm) {
            log(`token issuer is undefined, realm: ${realm}`);
        }
        else if (token.iss && realm && token.iss !== realm) {
            log(`token issuer is not realm, realm: ${realm}, issuer: ${token.iss}`);
        }
        // TODO: make an API call to validate token
        await setLoggedIn(jwt, token, redirectTo);
        if (propogate)
            sendMessage({ state: State.LoggedIn, value: true });
        return token;
    }
    catch {
        return null;
    }
};
export const setLoggedIn = async (jwt, token, redirectTo) => {
    await storeJwt(getSessionKey(), jwt);
    stateUsername.set(token.username ?? '');
    statePlayerId.set(token.player_id);
    if (redirectTo) {
        // this will be used by paths::handlePublicToPlayerRedirect called by public site layout
        log(`setting redirectTo: ${redirectTo}`);
        sessionStorage.setItem('redirectTo', redirectTo);
    }
    loggedIn.set(true);
};
export const logOut = async (event) => {
    showLoading.set(true);
    try {
        if (event)
            event.preventDefault();
        sessionStorage.removeItem('redirectTo');
    }
    catch {
        showLoading.set(true);
    }
    try {
        const result = await Logout({ variables: {}, context: { api: Api.public } });
        log(`logout result: ${JSON.stringify(result)}`);
    }
    catch (e) {
        console.log(`logout api error: ${e}`);
        showLoading.set(true);
    }
    await clearJwt(getSessionKey());
    try {
        await client.clearStore();
        await sleep(100);
    }
    catch (e) {
        console.log(`clearStore error: ${e}`);
        showLoading.set(true);
    }
    stateUsername.set('');
    statePlayerId.set(undefined);
    loggedIn.set(false);
    //console.log(`login state set to false`);
    isPlayerChatOpen.set(false);
    // Redirect to the absolute root URL to ensure we go to the home page and avoid rendering sensitive data
    if (cfgAppMode() !== AppMode.Public) {
        log(`reloading after logout....`);
        redirectNoloop(window.location.origin);
    }
    else {
        log(`expecting logout on public site`);
    }
    showLoading.set(true);
};
export const getCurrentToken = () => {
    return getSessionJwt();
};
const getSessionJwt = () => {
    const jwt = sessionStorage.getItem(getSessionKey());
    if (jwt) {
        return jwt;
    }
    if (jwt)
        return jwt;
    return null;
};
const initToken = async () => {
    // eslint-disable-next-line prefer-const
    let [jwt, token, tokenLocation] = await refreshJwt(getSessionKey());
    if (!jwt || !token) {
        log(`no tokens found`);
        loggedIn.set(false);
        return null;
    }
    const refreshedSession = await refreshSession(tokenLocation ?? 0);
    if (refreshedSession?.statusCode && refreshedSession?.statusCode !== Status.OK) {
        // todo: add tokenStatus to make this more explicit
        log(`refreshedSession statusCode: ${refreshedSession?.statusCode}`);
        await logOut();
        return null;
    }
    if (refreshedSession?.token) {
        try {
            token = jwtDecode(jwt);
            jwt = refreshedSession.token;
            log(`refresh token: ${JSON.stringify(token)}`);
        }
        catch (e) {
            log(`error decoding refreshed token: ${e}`);
        }
    }
    return await verifyToken(jwt, true, refreshedSession?.redirectTo ?? undefined);
};
if (typeof window !== 'undefined') {
    subscribeMessageState(async (message) => {
        if (message.state === State.LoggedIn) {
            if (message.value === true) {
                log(`global auth received loggedIn message`);
                const [jwt, token] = await getJwt(getSessionKey());
                if (jwt && token) {
                    await verifyToken(jwt, false);
                }
            }
            else if (message.value === false) {
                log(`global auth received loggedOut message`);
                await logOut();
                sendMessage({ state: State.LoggedOut, value: true });
            }
        }
    });
}
const brand = cfgBrand();
const getAppVersion = () => {
    if (typeof window === 'undefined')
        return 0;
    try {
        const appVersion = new URL(window.location.href).searchParams.get('appVersion');
        if (appVersion)
            return parseInt(VERSION.split('.')[2]) ?? 0;
    }
    catch {
        // console.log(`error acquiring appVersion`);
    }
    return 0;
};
const getLandingPage = () => {
    if (typeof window === 'undefined') {
        return new URL('http://localhost/');
    }
    if (window !== window.parent) {
        try {
            return new URL(parent.location.href);
        }
        catch (e) {
            log(`error acquiring framed href: ${e}`);
        }
    }
    return new URL(window.location.href);
};
// refresh session is called by initToken when app is first loaded
// todo: call when application comes back into focus after being backgrounded
// for a threshhold amount of time
export const refreshSession = async (tokenLocation) => {
    try {
        const publicKey = (await getStoredPublicKey()) ?? '';
        const page = getLandingPage();
        const landingDomain = page ? page.hostname : null;
        const landingPage = page ? page.pathname : null;
        const landingQs = page ? page.search : null;
        const variables = {
            brand,
            publicKey,
            fingerprint: null, // if we want fingerprints need to move fingerprint code outside of visit to avoid circular dependency and may need to wait for it
            landingDomain,
            landingPage,
            landingQs,
            siteLanguageId: get(locale),
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone ?? '',
            timeZoneOffset: new Date().getTimezoneOffset() ?? 0,
            timeNow: Date.now(),
            appVersion: getAppVersion(),
            lastActive: null,
            tokenLocation: tokenLocation ?? 0
        };
        const result = await RefreshSession({ variables, context: { api: Api.player } });
        const refreshedSession = result.data?.refreshSession;
        if (refreshedSession) {
            log(`refreshedSession: ${JSON.stringify(refreshedSession)}`);
            if (refreshedSession?.forceLogout) {
                log(`force logout`);
                await clearJwt(getSessionKey());
                await logOut();
            }
            else if (refreshedSession?.redirectTo) {
                //todo: do in a way that doesn't cause a redirect loop
                log(`redirect to ${refreshedSession.redirectTo}`);
                // } else if (refreshedSession?.forceReload) {
                // 	console.log(`force reload`);
                // 	window.location.reload();
                //  todo: do in a way that doesn't cause a refresh loop
            }
        }
        return refreshedSession;
    }
    catch (e) {
        console.log(`refreshSession error`, e);
    }
};
if (typeof window !== 'undefined') {
    initToken()
        .then(() => { })
        .catch((e) => console.log(e));
}
